import React, { useEffect, useMemo, useRef, useState } from 'react'
import injectSheet from 'react-jss'
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader
} from 'pobble-components/dist/modal'
import { apiSuccessCriteriaSearch } from '../../../../common/api/lesson'
import { errorHandler } from 'pobble-components/dist/error-handler'
import Button from 'pobble-components/dist/button'
import FormSelect from 'pobble-components/dist/form-elements/form-select'
import FormLabel from 'pobble-components/dist/form-elements/form-label'
import FormCheckbox from 'pobble-components/dist/form-elements/form-checkbox'
import { apiGetYearGroups } from '../../../../common/api/year-groups'
import Bone from 'pobble-components/dist/bone'
import { Table, Cell, TableRow } from 'pobble-components/dist/table'
import Spinner from 'pobble-components/dist/spinner'
import NoItemsPlaceholder from 'pobble-components/dist/no-items-placeholder'
import classNames from 'classnames'
import AgeGroup from 'pobble-components/dist/age-group'

const GENRES = [
  'Acrostic poem',
  'Advertising',
  'Autobiography',
  'Balanced Argument',
  'Biography',
  'Character description',
  'Diary entry',
  'Explanation text',
  'Fact file',
  'Fairy tale',
  'Formal letter',
  'Instructions',
  'Letter',
  'Narrative',
  'Newspaper report',
  'Non-chronological report',
  'Persuasion',
  'Persuasive letter',
  'Persuasive report',
  'Recount',
  'Story'
]

const LEVELS = ['Greater Depth', 'Expected', 'Working towards']

const formStyles = ({ COLORS, FONT_SIZES }) => ({
  content: {
    padding: '0',
    maxWidth: '700px'
  },
  head: {
    padding: '20px 20px 0',
    borderBottom: '1px solid #ececec'
  },
  filters: {
    background: '#fff',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gridGap: '16px'
  },
  selectButton: {
    marginTop: '16px'
  },
  body: {
    height: '400px',
    overflow: 'auto'
  },
  table: {
    padding: '0 0 20px',
    marginTop: '-1px'
  },
  criteria: {
    color: COLORS.TEXT.DARK_GREY.NORMAL
  },
  criteriaContainer: {
    '@media (min-width: 541px)': {
      display: 'flex'
    }
  },
  criteriaHolder: {
    flex: '1',
    marginTop: '-4px'
  },
  prompt: {
    display: 'block',
    marginTop: '4px',
    color: COLORS.TEXT.LIGHT_GREY.NORMAL,
    ...FONT_SIZES.BODY_SMALL.STYLE
  },
  ageGroupHolder: {
    marginTop: '16px',
    '@media (min-width: 541px)': {
      margin: '0 16px'
    }
  },
  checkboxCell: {
    width: '60px',
    textAlign: 'center',
    paddingRight: '0',
    verticalAlign: 'top'
  },
  holder: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  loadMoreIndication: {
    display: 'flex',
    justifyContent: 'center',
    padding: '10px 0 30px',
    '& > *': {
      color: COLORS.TEXT.LIGHT_GREY.NORMAL,
      fontSize: '20px'
    }
  },
  loadingMore: {
    '& > *': {
      color: COLORS.BLUE.NORMAL
    }
  }
})

const SuggestedForm = injectSheet(formStyles)(
  ({ classes, selectedYearGroup, requestClose, onAdd }) => {
    const defaultFilters = useMemo(
      () => {
        return {
          genre: null,
          level: null,
          year_group_id: selectedYearGroup || null
        }
      },
      [selectedYearGroup]
    )

    const containerRef = useRef(null)
    const [yearGroups, setYearGroups] = useState(null)
    const yearGroupsMap = useMemo(
      () => {
        if (!yearGroups) return null
        const result = {}
        yearGroups.forEach(yearGroup => (result[yearGroup.id] = yearGroup))
        return result
      },
      [yearGroups]
    )
    const [searchResult, setSearchResult] = useState(null)
    const [filters, setFilters] = useState(defaultFilters)
    const [selected, setSelected] = useState({})
    const [loadMoreState, setLoadMoreState] = useState({
      pending: false,
      everythingFetched: false
    })

    const selectedArray = useMemo(
      () => {
        return Object.keys(selected)
          .filter(key => !!selected[key])
          .map(key => selected[key])
      },
      [selected]
    )

    const loadMore = () => {
      if (!loadMoreState.pending && !loadMoreState.everythingFetched) {
        setLoadMoreState({ ...loadMoreState, pending: true })
        apiSuccessCriteriaSearch({
          ...filters,
          from: searchResult.length,
          limit: 50
        })
          .then(res => {
            setLoadMoreState({
              pending: false,
              everythingFetched: res.data.length < 50
            })
            setSearchResult([...searchResult, ...res.data])
          })
          .catch(errorHandler)
      }
    }

    useEffect(() => {
      apiGetYearGroups({
        include: 'year_group.names,year_group.order'
      })
        .then(res =>
          setYearGroups(
            res.data.year_groups.filter(yg => yg.order >= 3 && yg.order <= 8)
          )
        )
        .catch(errorHandler)
    }, [])

    useEffect(
      () => {
        setSearchResult(null)
        apiSuccessCriteriaSearch({
          ...filters,
          limit: 50
        })
          .then(res => {
            setLoadMoreState({
              pending: false,
              everythingFetched: res.data.length < 50
            })
            setSearchResult(res.data)
          })
          .catch(errorHandler)
      },
      [filters]
    )

    useEffect(
      () => {
        const node = containerRef.current
        const handler = () => {
          if (node.scrollTop === node.scrollHeight - node.offsetHeight) {
            loadMore()
          }
        }
        node.addEventListener('scroll', handler)
        return () => {
          node.removeEventListener('scroll', handler)
        }
      },
      [searchResult] // eslint-disable-line
    )

    return (
      <form>
        <ModalContent className={classes.content}>
          <div className={classes.head}>
            <div className={classes.filters}>
              <div>
                <FormLabel htmlFor="genre">Genre</FormLabel>
                <FormSelect
                  value={filters.genre || ''}
                  onChange={e =>
                    setFilters({ ...filters, genre: e.target.value || null })
                  }
                  id="genre"
                  name="genre">
                  <option value="">All</option>
                  {GENRES.map(genre => <option value={genre}>{genre}</option>)}
                </FormSelect>
              </div>
              <div>
                <FormLabel htmlFor="ageGroup">Age Group</FormLabel>
                {!yearGroups && <Bone btn />}
                {!!yearGroups && (
                  <FormSelect
                    value={filters.year_group_id || ''}
                    onChange={e =>
                      setFilters({
                        ...filters,
                        year_group_id: e.target.value || null
                      })
                    }
                    id="ageGroup"
                    name="ageGroup">
                    <option value="">All</option>
                    {yearGroups.map(yg => (
                      <option value={yg.id}>{yg.title}</option>
                    ))}
                  </FormSelect>
                )}
              </div>
              <div>
                <FormLabel htmlFor="level">Levels</FormLabel>
                <FormSelect
                  value={filters.level || ''}
                  id="level"
                  name="level"
                  onChange={e =>
                    setFilters({ ...filters, level: e.target.value || null })
                  }>
                  <option value="">All</option>
                  {LEVELS.map(level => <option value={level}>{level}</option>)}
                </FormSelect>
              </div>
            </div>
            <Button
              action={() => {
                if (!selectedArray.length) {
                  const selected = {}
                  searchResult.forEach(
                    result => (selected[result.criteria] = result)
                  )
                  setSelected(selected)
                } else {
                  setSelected({})
                }
              }}
              tertiary
              tall
              bleed
              className={classes.selectButton}>
              {!searchResult || !searchResult.length ? (
                <span>&nbsp;</span>
              ) : !selectedArray.length ? (
                'Select all'
              ) : (
                'Deselect all'
              )}
            </Button>
          </div>
          <div ref={containerRef} className={classes.body}>
            {!searchResult && (
              <div className={classes.holder}>
                <Spinner />
              </div>
            )}
            {searchResult &&
              !!searchResult.length && (
                <>
                  <Table className={classes.table}>
                    <tbody>
                      {searchResult.map(result => {
                        return (
                          <TableRow>
                            <Cell className={classes.checkboxCell}>
                              <FormCheckbox
                                checked={!!selected[result.criteria]}
                                onChange={() =>
                                  setSelected({
                                    ...selected,
                                    [result.criteria]: selected[result.criteria]
                                      ? null
                                      : result
                                  })
                                }
                              />
                            </Cell>
                            <Cell className={classes.criteria}>
                              <div className={classes.criteriaContainer}>
                                <div className={classes.criteriaHolder}>
                                  {result.criteria}
                                  {result.prompt && (
                                    <span className={classes.prompt}>
                                      prompt: {result.prompt}
                                    </span>
                                  )}
                                </div>
                                <div className={classes.ageGroupHolder}>
                                  {!!yearGroupsMap &&
                                    yearGroupsMap[result.year_group_id] && (
                                      <AgeGroup
                                        data={
                                          yearGroupsMap[result.year_group_id]
                                        }
                                      />
                                    )}
                                </div>
                              </div>
                            </Cell>
                          </TableRow>
                        )
                      })}
                    </tbody>
                  </Table>
                  {!loadMoreState.everythingFetched && (
                    <div
                      className={classNames(classes.loadMoreIndication, {
                        [classes.loadingMore]: loadMoreState.pending
                      })}>
                      <Spinner />
                    </div>
                  )}
                </>
              )}
            {searchResult &&
              !searchResult.length && (
                <div className={classes.holder}>
                  <NoItemsPlaceholder>
                    <p>We couldn't find anything</p>
                    <Button
                      tall
                      simple
                      action={() => setFilters(defaultFilters)}>
                      Reset filters
                    </Button>
                  </NoItemsPlaceholder>
                </div>
              )}
          </div>
        </ModalContent>
        <ModalFooter alignRight childrenGap>
          <Button tall simple action={requestClose}>
            Cancel
          </Button>
          <Button
            action={() => {
              onAdd(selectedArray)
              requestClose()
            }}
            disabled={!selectedArray.length}
            tall>
            Add {selectedArray.length || ''} selected
          </Button>
        </ModalFooter>
      </form>
    )
  }
)

export const SuggestedModal = ({
  classes,
  selectedYearGroup,
  onAdd,
  ...props
}) => {
  return (
    <Modal {...props} closeButton>
      <ModalHeader>Suggested success criteria</ModalHeader>
      <SuggestedForm
        onAdd={onAdd}
        selectedYearGroup={selectedYearGroup}
        requestClose={props.requestClose}
      />
    </Modal>
  )
}
